import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Redirect, Route, Switch, useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {ConfigProvider} from 'antd';
import {IntlProvider} from "react-intl";

import AppLocale from "../../lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
import SignUp from "../SignUp";
import ConfirmEmailSignUp from "../ConfirmEmailSignUp";
import PasswordResetEmail from "../PasswordResetEmail";
import PasswordResetConfirm from "../PasswordResetConfirm";
import {getAccessPath, setInitUrl} from "../../appRedux/actions";

import CircularProgress from "../../components/CircularProgress";

import {
    LAYOUT_TYPE_BOXED,
    LAYOUT_TYPE_FRAMED,
    LAYOUT_TYPE_FULL,
    NAV_STYLE_ABOVE_HEADER,
    NAV_STYLE_BELOW_HEADER,
    NAV_STYLE_DARK_HORIZONTAL,
    NAV_STYLE_DEFAULT_HORIZONTAL,
    NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
    THEME_TYPE_DARK
} from "../../constants/ThemeSetting";

const RestrictedRoute = ({component: Component, location, authUser, ...rest}) =>
    <Route
        {...rest}
        render={props =>
            authUser
                ? <Component {...props} />
                : <Redirect
                    to={{
                        pathname: '/signin',
                        state: {from: location}
                    }}
                />}
    />;

const setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
        document.body.classList.remove('boxed-layout');
        document.body.classList.remove('framed-layout');
        document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
        document.body.classList.remove('full-layout');
        document.body.classList.remove('framed-layout');
        document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
        document.body.classList.remove('boxed-layout');
        document.body.classList.remove('full-layout');
        document.body.classList.add('framed-layout');
    }
};

const setNavStyle = (navStyle) => {
    if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
        navStyle === NAV_STYLE_DARK_HORIZONTAL ||
        navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
        navStyle === NAV_STYLE_ABOVE_HEADER ||
        navStyle === NAV_STYLE_BELOW_HEADER) {
        document.body.classList.add('full-scroll');
        document.body.classList.add('horizontal-layout');
    } else {
        document.body.classList.remove('full-scroll');
        document.body.classList.remove('horizontal-layout');
    }
};

const App = () => {
    const locale = useSelector(({settings}) => settings.locale);
    const navStyle = useSelector(({settings}) => settings.navStyle);
    const layoutType = useSelector(({settings}) => settings.layoutType);
    const themeType = useSelector(({settings}) => settings.themeType);
    const accessPath = useSelector(({settings}) => settings.accessPath);
    const isDirectionRTL = useSelector(({settings}) => settings.isDirectionRTL);
    const initURL = useSelector(({settings}) => settings.initURL);

    const {isLoadingUser, authUser} = useSelector(({auth}) => auth);
    const dispatch = useDispatch();

    const location = useLocation();
    const history = useHistory();
    const match = useRouteMatch();

    useEffect(() => {
        if (isDirectionRTL) {
            document.documentElement.classList.add('rtl');
            document.documentElement.setAttribute('data-direction', 'rtl')
        } else {
            document.documentElement.classList.remove('rtl');
            document.documentElement.setAttribute('data-direction', 'ltr')
        }
    }, [isDirectionRTL]);

    useEffect(() => {
        if (locale)
            document.documentElement.lang = locale.locale;
    }, [locale]);

    useEffect(() => {
        if (themeType === THEME_TYPE_DARK) {
            document.body.classList.add('dark-theme');
        } else if (document.body.classList.contains('dark-theme')) {
            document.body.classList.remove('dark-theme');
        }
    }, [themeType]);

    useEffect(() => {
        if (initURL === '') {
            dispatch(setInitUrl(location.pathname));
        }
    });

    useEffect(() => {
        if (authUser) {
            const fetchAccessPath = async () => {
                await dispatch(getAccessPath({domain: window.location.hostname}))
            }
            fetchAccessPath()
        }
    }, [authUser]);

    useEffect(() => {
        if (!isLoadingUser && !authUser) {
            if (
                initURL.includes('/activate/') ||
                initURL.includes('/forgot-password') ||
                initURL.includes('/password/reset/confirm/')
            ) {
                history.push(initURL);
            } else {
                history.push('/signin');
            }
        }
    }, [isLoadingUser, authUser, initURL, history]);

    // Check the CURRENT page, not initURL (the page the app was first opened on):
    // otherwise after a login from /signin every authUser update threw the user to /sample,
    // and a second login stayed on /signin when initURL happened to be an allowed page.
    useEffect(() => {
        if (!isLoadingUser && authUser) {
            const allowedPaths = accessPath.map(item => item.path);
            if (allowedPaths.length > 0 && !allowedPaths.includes(location.pathname)) {
                history.push('/sample');
            }
        }
    }, [isLoadingUser, authUser, location.pathname, history, accessPath]);

    useEffect(() => {
        setLayoutType(layoutType);
        setNavStyle(navStyle);
    }, [layoutType, navStyle]);

    const currentAppLocale = AppLocale[locale.locale];

    return isLoadingUser ? <CircularProgress/> : (
        <ConfigProvider locale={currentAppLocale.antd} direction={isDirectionRTL ? 'rtl' : 'ltr'}>
            <IntlProvider
                locale={currentAppLocale.locale}
                messages={currentAppLocale.messages}>
                <Switch>
                    <Route exact path='/signin' component={SignIn}/>
                    <Route exact path='/signup' component={SignUp}/>
                    <Route exact path='/activate/:uid/:token' component={ConfirmEmailSignUp}/>
                    <Route exact path='/forgot-password' component={PasswordResetEmail}/>
                    <Route exact path='/password/reset/confirm/:uid/:token' component={PasswordResetConfirm}/>
                    <RestrictedRoute path={`${match.url}`} authUser={authUser} location={location} component={MainApp}/>
                </Switch>
            </IntlProvider>
        </ConfigProvider>
    )
};

export default App;
